/**
 * A block held to the last floor: dense lots, most shells two storeys, the fight upstairs.
 *
 * The third archetype, and the one that proves the vertical knobs are data. Neither the suburb
 * nor the yard leans on a storey, an opening or an upstairs share; this place turns all of them
 * up and touches no stage doing it.
 *
 * The same hundred and sixty metres as the other two, so a hundred soak seeds cost what they cost
 * there. What makes it a different place is grain: narrow streets, lots barely wider than a shell,
 * and the ruins standing shoulder to shoulder along both sides.
 */

import type { ZoneArchetype } from '@/sim/worldgen/zoneArchetypeContract';

export const FORTIFIED_BLOCK: ZoneArchetype = {
  id: 'fortifiedBlock',
  label: 'Fortified block',
  surfaces: {
    // Rubble underfoot: what a block that fought house to house is paved with once it is over.
    ground: 'weatheredConcrete',
    structure: 'weatheredConcrete',
    street: 'crackedAsphalt',
  },
  extent: {
    halfWidth: 80,
    halfDepth: 80,
    // Clears a two storey shell with room over it, the way the suburb's does.
    ceiling: 12,
  },
  densities: {
    // Half again the suburb's rate against smaller lots, so nearly every lot is built on.
    buildings: 0.6,
    // Barricades and rubble thick on the ground: every street here was a line once.
    props: 3.5,
    // A little over the suburb's, and most of it upstairs.
    enemies: 0.18,
    loot: 0.12,
  },
  storeys: {
    // Two, which is what the buildings carry today. Stated as the ceiling the place was built to
    // rather than as what the kit can stand: a taller building registered tomorrow earns its
    // floors here first.
    maxStoreys: 3,
    // Nearly every shell that can hold a stair gets its floor: the block is a row of upstairs.
    upperStoreyChance: 0.85,
    // A quarter of every upper floor gone: the fighting came through the ceilings.
    openingShare: 0.25,
    // Over half the shooters wait upstairs. The whole point of the place, and the knob #294
    // exists to prove moves the world.
    upstairsEnemyShare: 0.55,
    upstairsLootShare: 0.35,
  },
  relief: {
    amplitudeMetres: 0.4,
    cellMetres: 6,
    dips: 2,
  },
  navigability: {
    // The plan measures a little under six sevenths walkable across every seed tried, less than
    // the suburb for the shells packed along every street. The band is wide on both sides for
    // the reason the other two are: it catches a stage that broke, never a density nudged.
    minWalkableFraction: 0.55,
    maxWalkableFraction: 0.95,
  },
  layout: {
    // Tighter than the suburb's, so four districts span the zone rather than three.
    districtSpanMetres: { min: 34, max: 44 },
    // One lane and a kerb. Nothing turns a corner here at speed.
    streetWidthMetres: 6,
    // A shell and its yard at the bottom; over twice the minimum, so no span is stranded.
    lotSpanMetres: { min: 9, max: 20 },
    edgeBandMetres: 6,
    // Fewer loops than the yard: a block is a warren, and a dead end is a place to hold.
    loopEdgeFraction: 0.3,
  },
};
